"use client";

/**
 * Video Renderer
 *
 * Renders a standalone embedded YouTube/Vimeo player (16:9) with an optional caption.
 * The URL is converted to a safe, allowlisted embed URL via `toVideoEmbedUrl` — an
 * unsupported or invalid URL never reaches the iframe `src` (renders a placeholder in the
 * builder, nothing on the public page).
 */

import type React from "react";
import { useMemo } from "react";
import { Video as VideoIcon } from "lucide-react";

import { toVideoEmbedUrl } from "../../../_logic/link-utils";
import type { VideoComponent } from "../../../_types";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { SectionContainer } from "./_shared/SectionContainer";

interface VideoRendererProps {
  component: VideoComponent;
}

/**
 * Renders a video embed section for a {@link VideoComponent}.
 *
 * @param props - The video component to render.
 * @returns The video section element, or null on the public page when no valid URL is set.
 */
export function VideoRenderer({ component }: VideoRendererProps): React.ReactElement | null {
  const { isPublic } = usePublicViewContext();
  const embedUrl = useMemo(
    () => (component.videoUrl ? toVideoEmbedUrl(component.videoUrl) : null),
    [component.videoUrl]
  );
  const caption = component.caption?.trim();

  // Public page: a section with no valid video has nothing to show.
  if (isPublic && !embedUrl) return null;

  return (
    <SectionContainer
      maxWidth="4xl"
      background={component.background}
      title={component.title}
      componentId={component.id}
      titleFieldId="video-title"
    >
      {embedUrl ? (
        <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
          <iframe
            src={embedUrl}
            title={component.title || caption || "Embedded video"}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        </div>
      ) : (
        // Builder-only placeholder (public returns null above). Its own opaque bg-gray-50
        // surface, so text stays plain gray regardless of the section's own background.
        <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500">
          <VideoIcon className="h-8 w-8" aria-hidden="true" />
          <p className="text-sm font-medium">
            {component.videoUrl ? "Unsupported video URL" : "Add a YouTube or Vimeo link in the editor"}
          </p>
        </div>
      )}
      {caption && <p className="text-center text-sm font-medium text-[color:var(--lp-text,#6b7280)]">{caption}</p>}
    </SectionContainer>
  );
}
